/**
 * ProgressBar Component
 *
 * Displays overall progress through the questionnaire
 * Shows:
 * - Visual progress bar
 * - Question count (answered / total)
 * - Percentage complete
 * - Save status indicator
 */

import React from "react";

interface ProgressBarProps {
  answeredQuestions: number;
  totalQuestions: number;
  saving?: boolean;
  lastSaved?: Date;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  answeredQuestions,
  totalQuestions,
  saving = false,
  lastSaved,
}) => {
  const percentage = Math.round((answeredQuestions / totalQuestions) * 100);

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              AI Implementation Readiness Assessment
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              {answeredQuestions} of {totalQuestions} questions answered (
              {percentage}%)
            </p>
          </div>

          {/* Save Status */}
          <div className="flex items-center gap-2">
            {saving ? (
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </div>
            ) : lastSaved ? (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Saved
              </div>
            ) : null}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative">
          <div className="overflow-hidden h-3 text-xs flex rounded-full bg-gray-200">
            <div
              style={{ width: `${percentage}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
            />
          </div>

          {/* Milestone Markers */}
          {[25, 50, 75].map((milestone) => (
            <div
              key={milestone}
              className="absolute top-0 bottom-0 w-0.5 bg-white"
              style={{ left: `${milestone}%` }}
              title={`${milestone}% Complete`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
